'use client'

/**
 * The Money Map — market-cap flow dashboard. SSR hands the first payload (SEO + instant paint); the
 * client then polls for live updates. Sections: headline totals (added / wiped / net), a by-sector
 * diverging-bar flow, a cap-weighted heat wall of the biggest names, and biggest single-name swings,
 * plus a one-click "Share on X" that composes the viral post.
 */

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { TrendingUp, TrendingDown, Share2, ArrowUpRight, ArrowDownRight, Radio } from 'lucide-react'
import { SECTOR_ACCENT, type Sector } from '@/lib/dashboard/sectorMap'
import type { MoneyMapPayload, NameFlow, SectorFlow, Period } from '@/lib/dashboard/moneyMap'
import MoneyTreemap from './MoneyTreemap'
import { whenVisible } from '@/lib/whenVisible'

const PERIODS: { v: Period; label: string; word: string }[] = [
  { v: '1d', label: 'Today', word: 'today' },
  { v: '1w', label: '1W', word: 'this week' },
  { v: '1m', label: '1M', word: 'this month' },
]

const SESSION_LABEL: Record<MoneyMapPayload['session'], string> = { pre: 'Pre-market', rth: 'Market open', post: 'After hours', closed: 'Market closed' }

function usd(n: number, signed = false): string {
  const a = Math.abs(n)
  const sign = n < 0 ? '-' : signed ? '+' : ''
  if (a >= 1e12) return `${sign}$${(a / 1e12).toFixed(2)}T`
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(a >= 1e11 ? 0 : 1)}B`
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(0)}M`
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(0)}K`
  return `${sign}$${Math.round(a)}`
}
const pctStr = (p: number | null) => (p == null ? '' : `${p >= 0 ? '+' : ''}${p.toFixed(1)}%`)

function composeTweet(d: MoneyMapPayload): string {
  const date = new Date(d.generatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  // Owner style: keep the 🟢/🔴 circles only, no other emojis. Lead with what's working and what's getting
  // hit (by sector), then the top names with their % and $-cap move.
  const green = d.sectors.filter((s) => s.net > 0).sort((a, b) => b.net - a.net).slice(0, 3).map((s) => `${s.sector} ${usd(s.net, true)}`)
  const red = d.sectors.filter((s) => s.net < 0).sort((a, b) => a.net - b.net).slice(0, 3).map((s) => `${s.sector} ${usd(s.net, true)}`)
  const row = (m: MoneyMapPayload['gainers'][number]) => `$${m.symbol} ${pctStr(m.dayPct)} ${usd(m.capChange, true)}`
  const topG = d.gainers.slice(0, 4).map(row).join(', ')
  const topL = d.losers.slice(0, 4).map(row).join(', ')
  const lines = [
    `The Money Map · ${date}`,
    '',
    `U.S. stocks: ${usd(d.totals.added)} added, ${usd(Math.abs(d.totals.wiped))} wiped. Net ${usd(d.totals.net, true)}.`,
  ]
  if (green.length) lines.push('', `Doing well: ${green.join(', ')}`)
  if (red.length) lines.push(`Getting hit: ${red.join(', ')}`)
  if (topG) lines.push('', `🟢 ${topG}`)
  if (topL) lines.push(`🔴 ${topL}`)
  const tags = [...new Set([d.gainers[0]?.symbol, d.losers[0]?.symbol, 'SPY', 'QQQ'].filter(Boolean))].map((t) => `$${t}`).join(' ')
  lines.push('', 'Full live map on TheDesperateTrader', '', tags)
  return lines.join('\n')
}

const MAP_URL = 'https://thedesperatetrader.com/money-map'
// The three separately-branded panels (owner 2026-07-15: "multiple image downloads, each dashboard in
// its own image with TheDesperateTrader top-left"). Each is a real screenshot of one dashboard.
const PANELS = [
  { key: 'flow', file: 'the-money-map-1-sector-flow.png' },
  { key: 'map', file: 'the-money-map-2-heat-map.png' },
  { key: 'gains', file: 'the-money-map-3-biggest-swings.png' },
] as const

function triggerDownload(href: string, file: string) {
  try {
    const a = document.createElement('a')
    a.href = href; a.download = file
    document.body.appendChild(a); a.click(); a.remove()
  } catch { /* ignore */ }
}

// Download all three panels. Fetch each as a blob first (so the browser saves the real PNG rather than
// navigating), then stagger the saves — some browsers throttle rapid programmatic downloads.
async function downloadAllPanels() {
  for (let i = 0; i < PANELS.length; i++) {
    const p = PANELS[i]
    try {
      const res = await fetch(`/api/shot/money-map?panel=${p.key}`, { cache: 'no-store' })
      if (!res.ok) continue
      const url = URL.createObjectURL(await res.blob())
      triggerDownload(url, p.file)
      setTimeout(() => URL.revokeObjectURL(url), 4000)
    } catch { /* skip this panel */ }
    if (i < PANELS.length - 1) await new Promise((r) => setTimeout(r, 700))
  }
}

// Share to any platform. X gets link-free text + the downloaded image to attach (X throttles links);
// Reddit/Facebook/LinkedIn take the URL and auto-unfurl the OG image; StockTwits/Instagram have no web
// intent, so we just download the image for a manual post.
function shareTo(platform: string, d: MoneyMapPayload) {
  const text = composeTweet(d)
  const title = `The Money Map - U.S. stocks net ${usd(d.totals.net, true)} today`
  const u = encodeURIComponent(MAP_URL)
  switch (platform) {
    case 'x': void downloadAllPanels(); window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer'); break
    case 'reddit': window.open(`https://www.reddit.com/submit?url=${u}&title=${encodeURIComponent(title)}`, '_blank', 'noopener,noreferrer'); break
    case 'facebook': window.open(`https://www.facebook.com/sharer/sharer.php?u=${u}`, '_blank', 'noopener,noreferrer'); break
    case 'linkedin': window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${u}`, '_blank', 'noopener,noreferrer'); break
    case 'stocktwits': void downloadAllPanels(); window.open('https://stocktwits.com/', '_blank', 'noopener,noreferrer'); break
    case 'download': void downloadAllPanels(); break
    case 'copy': try { navigator.clipboard.writeText(`${text}\n${MAP_URL}`) } catch { /* ignore */ } break
  }
}

function ShareMenu({ d }: { d: MoneyMapPayload }) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onDoc); return () => document.removeEventListener('mousedown', onDoc)
  }, [])
  const items: { key: string; label: string; color: string; hint?: string }[] = [
    { key: 'x', label: 'X (Twitter)', color: '#e7e9ea', hint: '+ 3 images' },
    { key: 'reddit', label: 'Reddit', color: '#ff4500' },
    { key: 'facebook', label: 'Facebook', color: '#1877f2' },
    { key: 'linkedin', label: 'LinkedIn', color: '#0a66c2' },
    { key: 'stocktwits', label: 'StockTwits', color: '#40a5f7', hint: '+ 3 images' },
    { key: 'download', label: 'Download 3 images', color: '#a1a1aa', hint: 'flow · map · swings' },
    { key: 'copy', label: copied ? 'Copied!' : 'Copy caption', color: '#a1a1aa' },
  ]
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-sky-500/30 bg-sky-500/10 px-2.5 py-1 font-semibold text-sky-300 transition-colors hover:bg-sky-500/20">
        <Share2 className="h-3.5 w-3.5" /> Share
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-1.5 w-56 rounded-xl border border-zinc-700 bg-zinc-900/[0.98] p-1.5 shadow-2xl shadow-black/60 backdrop-blur">
          <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Share this map</div>
          {items.map((it) => (
            <button key={it.key} onClick={() => { shareTo(it.key, d); if (it.key === 'copy') { setCopied(true); setTimeout(() => setCopied(false), 1500) } else setOpen(false) }}
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] text-zinc-200 transition-colors hover:bg-white/5">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: it.color }} />
              <span className="font-semibold">{it.label}</span>
              {it.hint && <span className="ml-auto text-[10px] text-zinc-500">{it.hint}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function MoneyMapDashboard({ initial, shotMode = false, shotPanel }: { initial: MoneyMapPayload; shotMode?: boolean; shotPanel?: 'flow' | 'map' | 'gains' }) {
  const [data, setData] = useState<MoneyMapPayload>(initial)
  const [ago, setAgo] = useState(0)
  const [period, setPeriod] = useState<Period>('1d')
  const lastAt = useRef(initial.generatedAt)

  useEffect(() => {
    if (shotMode) return   // screenshot capture: freeze on the SSR snapshot, no polling
    let alive = true
    const refresh = async () => {
      try {
        const r = await fetch(`/api/dashboard/money-map?period=${period}`, { cache: 'no-store' })
        if (!r.ok) return
        const body: MoneyMapPayload = await r.json()
        if (!alive) return
        setData(body); lastAt.current = body.generatedAt
      } catch { /* keep last */ }
    }
    if (period !== '1d') refresh()   // 1d is the SSR initial; other periods fetch immediately
    const poll = setInterval(whenVisible(refresh), 45_000)
    const tick = setInterval(() => setAgo(Math.max(0, Math.round((Date.now() - lastAt.current) / 1000))), 1000)
    return () => { alive = false; clearInterval(poll); clearInterval(tick) }
  }, [period, shotMode])

  const [sector, setSector] = useState<Sector | 'All'>('All')
  const pickSector = (s: Sector) => setSector((cur) => (cur === s ? 'All' : s))
  const periodWord = PERIODS.find((p) => p.v === period)?.word ?? 'today'

  const { totals, sectors, gainers, losers, map } = data
  const netUp = totals.net >= 0
  const breadthTotal = Math.max(1, totals.advancers + totals.decliners)
  const advPct = (totals.advancers / breadthTotal) * 100

  // Panel gating: the live page shows everything. A shot with no shotPanel is the legacy combined image
  // (totals + flow + map). A shot WITH a shotPanel renders exactly one dashboard so Share can hand the
  // user three clean, separately-branded images (owner 2026-07-15).
  const showHeader = !shotMode || shotPanel == null || shotPanel === 'flow'
  const showFlow = !shotMode || shotPanel == null || shotPanel === 'flow'
  const showMap = !shotMode || shotPanel == null || shotPanel === 'map'
  const showMovers = shotMode ? shotPanel === 'gains' : true

  return (
    <div>
      {/* ── Headline totals ─────────────────────────────────────────────────── */}
      {showHeader && (<>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="Added" value={usd(totals.added, true)} tone="up" icon={<TrendingUp className="h-4 w-4" />} />
        <StatCard label="Wiped" value={usd(totals.wiped)} tone="down" icon={<TrendingDown className="h-4 w-4" />} />
        <StatCard label="Net" value={usd(totals.net, true)} tone={netUp ? 'up' : 'down'} big
          icon={netUp ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />} />
      </div>

      {/* pulse strip */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-zinc-800/70 bg-zinc-900/40 px-4 py-2.5 text-xs text-zinc-400">
        <span className={`inline-flex items-center gap-1.5 font-semibold ${data.session === 'rth' ? 'text-emerald-300' : 'text-zinc-300'}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${data.session === 'rth' ? 'animate-pulse bg-emerald-400' : 'bg-zinc-500'}`} />
          {SESSION_LABEL[data.session]}
        </span>
        <span className="text-zinc-500">·</span>
        <span><span className="font-semibold text-zinc-200">{totals.universe.toLocaleString()}</span> stocks mapped</span>
        {/* breadth */}
        <span className="hidden items-center gap-2 sm:inline-flex">
          <span className="text-emerald-400">{totals.advancers} up</span>
          <span className="h-1.5 w-24 overflow-hidden rounded-full bg-rose-500/40"><span className="block h-full rounded-full bg-emerald-500" style={{ width: `${advPct}%` }} /></span>
          <span className="text-rose-400">{totals.decliners} down</span>
        </span>
        <span className="ml-auto flex items-center gap-3">
          <span className="inline-flex overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900/60 p-0.5">
            {PERIODS.map((p) => (
              <button key={p.v} onClick={() => setPeriod(p.v)}
                className={`rounded-md px-2 py-0.5 text-[11px] font-bold transition-colors ${period === p.v ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-zinc-200'}`}>{p.label}</button>
            ))}
          </span>
          <span className="hidden items-center gap-1 text-zinc-500 sm:inline-flex"><Radio className="h-3 w-3" /> {shotMode ? 'Live · updated every minute' : `Live · ${ago}s ago`}</span>
          {!shotMode && <ShareMenu d={data} />}
        </span>
      </div>
      </>)}

      {/* ── By-sector flow (diverging bars) — click to filter the map ───────── */}
      {showFlow && (
        <>
          <SectionTitle>Where the money moved {periodWord} · tap a sector to filter the map</SectionTitle>
          <div className="space-y-1.5">
            {sectors.map((s) => (
              <SectorBar key={s.sector} s={s} max={Math.max(...sectors.map((x) => Math.abs(x.net)), 1)}
                active={sector === s.sector} onClick={() => pickSector(s.sector)} />
            ))}
            {sectors.length === 0 && <Empty />}
          </div>
        </>
      )}

      {/* ── Cap-weighted treemap (size = market cap, color = move) ──────────── */}
      {showMap && (
        <>
          <SectionTitle>The map · tile size = market cap, color = today&apos;s move</SectionTitle>
          {map.length > 0 ? <MoneyTreemap items={map} filter={sector} onFilter={setSector} /> : <Empty />}
        </>
      )}

      {/* ── Biggest single-name swings — live page + the 'gains' share panel ── */}
      {showMovers && (
        <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 ${shotMode ? '' : 'mt-8'}`}>
          <MoversCard title="Biggest gains" tone="up" items={gainers} />
          <MoversCard title="Biggest losses" tone="down" items={losers} />
        </div>
      )}
    </div>
  )
}

function StatCard({ label, value, tone, icon, big }: { label: string; value: string; tone: 'up' | 'down'; icon: React.ReactNode; big?: boolean }) {
  const up = tone === 'up'
  return (
    <div className={`relative overflow-hidden rounded-2xl border p-4 ${up ? 'border-emerald-500/25 bg-emerald-500/[0.07]' : 'border-rose-500/25 bg-rose-500/[0.07]'} ${big ? 'sm:ring-1 sm:ring-white/10' : ''}`}>
      <div className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider ${up ? 'text-emerald-300' : 'text-rose-300'}`}>
        {icon} {label}
      </div>
      <div className={`mt-1 font-bold tabular-nums ${big ? 'text-3xl' : 'text-2xl'} ${up ? 'text-emerald-300' : 'text-rose-300'}`}>{value}</div>
    </div>
  )
}

function SectorBar({ s, max, active, onClick }: { s: SectorFlow; max: number; active: boolean; onClick: () => void }) {
  const up = s.net >= 0
  const w = Math.min(100, (Math.abs(s.net) / max) * 100)
  return (
    <button onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-lg px-2 py-1 text-left transition-colors hover:bg-white/5 ${active ? 'bg-white/10 ring-1 ring-white/20' : ''}`}>
      <div className={`w-36 shrink-0 truncate text-right text-xs font-semibold sm:w-40 ${SECTOR_ACCENT[s.sector]}`} title={s.sector}>{s.sector}</div>
      {/* diverging track: center line, bar grows left (red) or right (green) */}
      <div className="relative h-5 flex-1">
        <div className="absolute left-1/2 top-0 h-full w-px bg-zinc-700" />
        <div className={`absolute top-0 h-full rounded-sm ${up ? 'left-1/2 bg-emerald-500/70' : 'right-1/2 bg-rose-500/70'}`} style={{ width: `${w / 2}%` }} />
      </div>
      <div className={`w-20 shrink-0 text-right text-xs font-bold tabular-nums ${up ? 'text-emerald-300' : 'text-rose-300'}`}>{usd(s.net, true)}</div>
    </button>
  )
}

function MoversCard({ title, tone, items }: { title: string; tone: 'up' | 'down'; items: NameFlow[] }) {
  const up = tone === 'up'
  return (
    <div className="rounded-2xl border border-zinc-800/70 bg-zinc-900/40 p-4">
      <div className={`mb-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${up ? 'text-emerald-300' : 'text-rose-300'}`}>
        {up ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />} {title}
      </div>
      <ul className="space-y-1">
        {items.map((it) => (
          <li key={it.symbol}>
            <Link href={`/chart/${encodeURIComponent(it.symbol)}`} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-white/5">
              <span className="w-16 shrink-0 font-mono text-sm font-bold text-zinc-100">${it.symbol}</span>
              <span className={`hidden shrink-0 text-xs sm:inline ${SECTOR_ACCENT[it.sector]}`}>{it.sector}</span>
              <span className={`ml-auto shrink-0 text-xs tabular-nums ${(it.dayPct ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{pctStr(it.dayPct)}</span>
              <span className={`w-20 shrink-0 text-right text-sm font-bold tabular-nums ${it.capChange >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{usd(it.capChange, true)}</span>
            </Link>
          </li>
        ))}
        {items.length === 0 && <li className="px-2 py-3 text-sm text-zinc-500">No data yet.</li>}
      </ul>
    </div>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="mb-3 mt-8 text-xs font-semibold uppercase tracking-widest text-zinc-500">{children}</h2>
}
function Empty() {
  return <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 px-4 py-6 text-center text-sm text-zinc-500">Waiting for market data - check back during market hours.</div>
}
